import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";

const products = [
  { to: "/products/vyayanhub", label: "VyayanHub", desc: "Gym management, end to end", badge: "Live" },
  { to: "/products/society-management", label: "Society Management", desc: "For housing societies & RWAs", badge: "Soon" },
  { to: "/products/restaurant-management", label: "Restaurant Management", desc: "Front-of-house to kitchen", badge: "Soon" },
];

const services = [
  { to: "/services/custom-software-development", label: "Custom Software Development", desc: "Tools shaped to your operations" },
  { to: "/services/cloud-solutions", label: "Cloud Solutions", desc: "Infrastructure that stays invisible" },
  { to: "/services/salesforce-development", label: "Salesforce Development", desc: "Implementation, Apex & integration" },
  { to: "/services/data-and-ai", label: "Data & AI", desc: "LLM apps, document intelligence, Indian-language AI" },
];

function Wordmark() {
  return (
    <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-brand font-heading text-lg font-bold text-white">A</span>
      <span className="font-heading text-xl font-bold tracking-tight text-gray-950">
        Adika<span className="text-brand"> Tech</span>
      </span>
    </Link>
  );
}

function Dropdown({ label, items, testId, footerLink }) {
  return (
    <div className="group relative">
      <button
        data-testid={testId}
        className="flex items-center gap-1.5 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:text-gray-950 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" aria-hidden="true" />
      </button>
      <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 translate-y-2 pt-3 opacity-0 transition-[opacity,transform] duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <div className="w-[22rem] rounded-sm border border-gray-200 bg-white p-2 shadow-xl shadow-gray-950/5">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group/item flex items-start justify-between gap-3 rounded-sm px-4 py-3 transition-colors duration-200 hover:bg-brand-tint"
            >
              <span>
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-950">
                  {item.label}
                  {item.badge && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        item.badge === "Live" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block text-xs text-gray-500">{item.desc}</span>
              </span>
              <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-gray-300 transition-colors duration-200 group-hover/item:text-brand" aria-hidden="true" />
            </Link>
          ))}
          {footerLink && (
            <a
              href={footerLink.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={footerLink.testId}
              className="mt-1 flex items-center justify-between gap-3 rounded-sm border-t border-gray-100 px-4 py-3 text-xs font-semibold text-gray-500 transition-colors duration-200 hover:text-brand"
            >
              {footerLink.label}
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function MobileSection({ label, items, open, onToggle }) {
  return (
    <div className="border-b border-gray-100">
      <button
        data-testid={`mobile-nav-${label.toLowerCase()}`}
        onClick={onToggle}
        className="flex w-full items-center justify-between py-4 text-left font-heading text-lg font-semibold text-gray-950"
      >
        {label}
        <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="pb-4">
            {items.map((item) => (
              <Link key={item.to} to={item.to} className="flex items-center gap-2 py-2.5 text-sm text-gray-600" data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {item.label}
                {item.badge && (
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${item.badge === "Live" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
    setSection(null);
  }, [pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Wordmark />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          <Dropdown label="Products" items={products} testId="nav-products-dropdown" footerLink={{ href: "https://vyayamhub.com", label: "vyayamhub.com — the live product", testId: "nav-vyayamhub-external" }} />
          <Dropdown label="Services" items={services} testId="nav-services-dropdown" />
          <Link to="/about" data-testid="nav-about" className="text-sm font-medium text-gray-700 transition-colors duration-200 hover:text-gray-950">
            About
          </Link>
          <Link to="/insights" data-testid="nav-insights" className="text-sm font-medium text-gray-700 transition-colors duration-200 hover:text-gray-950">
            Insights
          </Link>
          <Link to="/contact" data-testid="nav-contact" className="text-sm font-medium text-gray-700 transition-colors duration-200 hover:text-gray-950">
            Contact
          </Link>
        </nav>
        <div className="hidden lg:block">
          <Link
            to="/products/vyayanhub#demo"
            data-testid="header-book-demo-button"
            className="rounded-sm bg-gray-950 px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
          >
            Book a Demo
          </Link>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="rounded-sm p-2 text-gray-900 lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <div className={`lg:hidden ${open ? "block" : "hidden"}`} data-testid="mobile-menu">
        <div className="border-t border-gray-100 px-5 pb-6 pt-2">
          <MobileSection label="Products" items={products} open={section === "products"} onToggle={() => setSection(section === "products" ? null : "products")} />
          <MobileSection label="Services" items={services} open={section === "services"} onToggle={() => setSection(section === "services" ? null : "services")} />
          <Link to="/about" className="block border-b border-gray-100 py-4 font-heading text-lg font-semibold text-gray-950" data-testid="mobile-nav-about">About</Link>
          <Link to="/insights" className="block border-b border-gray-100 py-4 font-heading text-lg font-semibold text-gray-950" data-testid="mobile-nav-insights">Insights</Link>
          <Link to="/contact" className="block border-b border-gray-100 py-4 font-heading text-lg font-semibold text-gray-950" data-testid="mobile-nav-contact">Contact</Link>
          <Link
            to="/products/vyayanhub#demo"
            data-testid="mobile-book-demo-button"
            className="mt-6 block rounded-sm bg-gray-950 px-5 py-3.5 text-center text-sm font-semibold text-white"
          >
            Book a Demo
          </Link>
        </div>
      </div>
    </header>
  );
}
